import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { ChartConfiguration } from 'chart.js';
import { MspDashboardPriority, MspPrioritiesResponse } from '../../interfaces/msp-dashboard-kpi';
import { ManagedServicesSharedDataService } from '../../../services/managed-services-shared-data.service';
import { environment } from 'src/environments/environment';
import { MSPDashboardV2Component } from '../msp-dashboard-v2.component';
import { finalize } from 'rxjs';

@Component({
  selector: 'app-dashboard-priorities',
  templateUrl: './dashboard-priorities.component.html',
  styleUrls: ['./dashboard-priorities.component.scss'],
  standalone: false,
})
export class DashboardPrioritiesComponent extends MSPDashboardV2Component implements OnChanges {
  @Input() dashboard: string;
  @Input() boards: string[] = [];
  @Output() boardsLoaded = new EventEmitter<string[]>();

  isFetching = false;
  errorFetching = false;
  priorities: MspDashboardPriority[] = [];
  currentTickets = 0;

  barChartData: ChartConfiguration<'bar'>['data'] = { labels: ['Tickets'], datasets: [] };
  barChartOptions: ChartConfiguration<'bar'>['options'] = {};

  constructor(
    private http: HttpClient,
    private router: Router,
    private managedServicesSharedDataService: ManagedServicesSharedDataService,
  ) {
    super();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['dashboard']?.currentValue) {
      this.fetch();
    }
  }

  filterByCurrentTickets() {
    this.managedServicesSharedDataService.resetFilters();
    this.managedServicesSharedDataService.filters.priorityIds = this.priorities.map((p) => p.id);
    this.navigate('open');
  }

  filterByPriority(priority: MspDashboardPriority) {
    this.managedServicesSharedDataService.resetFilters();
    this.managedServicesSharedDataService.filters.priorityIds = [priority.id];
    this.navigate('open');
  }

  retry() {
    this.fetch();
  }

  private fetch() {
    this.isFetching = true;
    this.isFetchingKpis.emit(true);
    this.errorFetching = false;

    this.http
      .get<MspPrioritiesResponse>(`${environment.restUrl}/v1/msp/dashboards/${this.dashboard}/priorities`)
      .pipe(
        finalize(() => {
          this.isFetching = false;
          this.isFetchingKpis.emit(false);
        }),
      )
      .subscribe({
        next: (response) => {
          this.boardsLoaded.emit(response.boards);
          this.priorities = response.priorities;
          this.currentTickets = response.currentTickets;
          this.barChartData = { labels: ['Tickets'], datasets: this.buildBarDatasets() };
          this.barChartOptions = {
            responsive: true,
            maintainAspectRatio: false,
            indexAxis: 'y',
            plugins: { legend: { display: false }, tooltip: { enabled: false } },
            hover: { mode: null },
            scales: {
              x: { stacked: true, display: false, max: this.currentTickets },
              y: { stacked: true, display: false },
            },
          };
        },
        error: () => (this.errorFetching = true),
      });
  }

  private buildBarDatasets() {
    const colors: Record<string, string> = {
      P1: '#c62828',
      P2: '#ff6a16',
      P3: '#f7cb00',
      P4: '#21b3ff',
      P5: '#00c100',
    };
    return this.priorities.map((p) => ({
      data: [p.count],
      backgroundColor: colors[p.label],
      stack: 'a',
      label: p.label,
    }));
  }

  private navigate(statusType: 'open' | 'closed' | null) {
    this.managedServicesSharedDataService.filters.boardNames = this.boards;
    this.managedServicesSharedDataService.filters.relationship = 'parent';
    this.managedServicesSharedDataService.filters.statusesGroup = statusType ? [statusType] : [];
    this.router.navigateByUrl(`/secured/managed-services/tickets?refresh=${Date.now()}`);
  }
}
